import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldAlert, ArrowLeft } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function fmt(iso) { try { return iso ? new Date(iso).toLocaleString() : "—"; } catch { return iso; } }

// Static admin request header. Hoisted to module scope so it is a
// stable reference and does not trigger react-hooks/exhaustive-deps
// warnings when used inside useCallback/useEffect below.
const withAdminHeaders = { headers: { "X-Admin-Request": "1" } };

export default function AdminDSAR() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/admin/dsar", { replace: true });
  }, [user, loading, navigate]);

  const [cases, setCases] = useState([]);
  const [busy, setBusy] = useState(false);
  const [erasureUserId, setErasureUserId] = useState("");
  const [erasureDry, setErasureDry] = useState(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applyConfirm, setApplyConfirm] = useState("");
  const [applying, setApplying] = useState(false);
  const [restoreDry, setRestoreDry] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [ownerDecisions, setOwnerDecisions] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/dsar/cases", withAdminHeaders);
      setCases(r.data.cases || []);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401 || s === 403 || s === 404) {
        toast.error("Admin access required.");
      } else {
        toast.error("Could not load DSAR cases.");
      }
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const loadOwnerDecisions = useCallback(async () => {
    try {
      const r = await api.get("/admin/dsar/owner_decisions", withAdminHeaders);
      setOwnerDecisions(r.data || {});
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (user) loadOwnerDecisions(); }, [user, loadOwnerDecisions]);

  const runErasureDryRun = useCallback(async () => {
    if (!erasureUserId) { toast.error("Enter a user_id first."); return; }
    try {
      const r = await api.post("/admin/dsar/erasure/dry_run", { user_id: erasureUserId }, withAdminHeaders);
      setErasureDry(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d || "Dry-run failed.");
    }
  }, [erasureUserId]);

  const submitApply = useCallback(async () => {
    if (applyConfirm !== "APPLY") { toast.error("Type APPLY to confirm."); return; }
    setApplying(true);
    try {
      const r = await api.post("/admin/dsar/erasure/apply",
        { user_id: erasureUserId, confirm: "APPLY", reason_code: "admin_action" },
        withAdminHeaders);
      toast.success(`Erasure status: ${r.data.status || "applied"}`);
      setApplyDialogOpen(false); setApplyConfirm("");
      runErasureDryRun();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "tombstone_salt_not_configured"
        ? "Tombstone salt not configured on the server."
        : (d || "Apply failed."));
    } finally { setApplying(false); }
  }, [applyConfirm, erasureUserId, runErasureDryRun]);

  const runRestoreDryRun = useCallback(async () => {
    try {
      const r = await api.post("/admin/dsar/restore/dry_run", {}, withAdminHeaders);
      setRestoreDry(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "tombstone_salt_not_configured"
        ? "Tombstone salt not configured on the server."
        : (d || "Restore dry-run failed."));
    }
  }, []);

  const submitRestoreApply = useCallback(async () => {
    if (restoreConfirm !== "RECONCILE") { toast.error("Type RECONCILE to confirm."); return; }
    try {
      const r = await api.post("/admin/dsar/restore/apply", { confirm: "RECONCILE" }, withAdminHeaders);
      toast.success(`Reconciled users: ${r.data.reprocessed_users}, usage events: ${r.data.reprocessed_usage_events}`);
      setRestoreDialogOpen(false); setRestoreConfirm("");
      runRestoreDryRun();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d || "Restore apply failed.");
    }
  }, [restoreConfirm, runRestoreDryRun]);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen flex flex-col" data-testid="admin-dsar-page">
      <NavBar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}
                  data-testid="admin-dsar-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-semibold">DSAR / Erasure Console</h1>
          <Badge variant="outline">Owner only</Badge>
        </div>

        <Alert className="mb-6" data-testid="admin-dsar-notice">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Destructive actions are dry-run by default</AlertTitle>
          <AlertDescription>
            Applying an erasure permanently deletes the user&apos;s data.
            Restore reconciliation re-applies erasure plans for restored
            accounts. Both require explicit confirmation phrases.
          </AlertDescription>
        </Alert>

        <Card className="mb-6" data-testid="admin-dsar-cases-card">
          <CardHeader>
            <CardTitle>Case queue</CardTitle>
            <CardDescription>
              Most recent 50 DSAR cases. Filter by status/type via the API.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {!busy && cases.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="admin-dsar-cases-empty">
                No cases yet.
              </p>
            )}
            {cases.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Opened</TableHead>
                    <TableHead>Decision</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cases.map(c => (
                    <TableRow key={c.case_id} data-testid={`admin-dsar-case-row-${c.case_id}`}>
                      <TableCell className="font-mono text-xs">{c.case_id}</TableCell>
                      <TableCell>{c.case_type}</TableCell>
                      <TableCell><Badge variant="outline">{c.status}</Badge></TableCell>
                      <TableCell>{fmt(c.opened_at)}</TableCell>
                      <TableCell>{c.decision_code || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="mb-6" data-testid="admin-dsar-erasure-card">
          <CardHeader>
            <CardTitle>Erasure execution</CardTitle>
            <CardDescription>
              Dry-run counts every collection that will be touched. Apply
              requires typing <code>APPLY</code>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label htmlFor="admin-erasure-uid">Target user_id</Label>
                <Input id="admin-erasure-uid" value={erasureUserId}
                       onChange={e => setErasureUserId(e.target.value)}
                       data-testid="admin-erasure-user-id-input" />
              </div>
              <Button onClick={runErasureDryRun} data-testid="admin-erasure-dry-run-btn">
                Run dry-run
              </Button>
              <Button variant="destructive" disabled={!erasureDry}
                      onClick={() => setApplyDialogOpen(true)}
                      data-testid="admin-erasure-apply-btn">
                Apply…
              </Button>
            </div>
            {erasureDry && (
              <pre className="text-xs whitespace-pre-wrap bg-muted p-3 rounded max-h-96 overflow-auto"
                   data-testid="admin-erasure-dry-run-output">
                {JSON.stringify(erasureDry, null, 2)}
              </pre>
            )}
          </CardContent>
        </Card>

        <Card className="mb-6" data-testid="admin-dsar-restore-card">
          <CardHeader>
            <CardTitle>Backup / restore reconciliation</CardTitle>
            <CardDescription>
              Detects restored erased-user rows and restored usage events.
              Apply blocks normal processing until reconciliation completes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Button onClick={runRestoreDryRun} data-testid="admin-restore-dry-run-btn">
                Restore dry-run
              </Button>
              <Button variant="destructive" disabled={!restoreDry}
                      onClick={() => setRestoreDialogOpen(true)}
                      data-testid="admin-restore-apply-btn">
                Apply reconciliation…
              </Button>
            </div>
            {restoreDry && (
              <pre className="text-xs whitespace-pre-wrap bg-muted p-3 rounded"
                   data-testid="admin-restore-dry-run-output">
                {JSON.stringify(restoreDry, null, 2)}
              </pre>
            )}
          </CardContent>
        </Card>

        <Card data-testid="admin-dsar-owner-decisions-card">
          <CardHeader>
            <CardTitle>Owner decision defaults</CardTitle>
            <CardDescription>
              Approved technical + owner-configurable accountability defaults.
              Legal review remains an Owner decision.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="text-xs whitespace-pre-wrap bg-muted p-3 rounded"
                 data-testid="admin-dsar-owner-decisions-output">
              {ownerDecisions ? JSON.stringify(ownerDecisions, null, 2) : "loading…"}
            </pre>
          </CardContent>
        </Card>

      </main>

      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent data-testid="admin-erasure-apply-dialog">
          <DialogHeader>
            <DialogTitle>Confirm erasure</DialogTitle>
            <DialogDescription>
              This will delete user data for <code>{erasureUserId}</code> across every
              matching collection. Type APPLY to confirm.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="apply-confirm">Type APPLY</Label>
            <Input id="apply-confirm" value={applyConfirm}
                   onChange={e => setApplyConfirm(e.target.value)}
                   data-testid="admin-erasure-apply-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyDialogOpen(false)}
                    data-testid="admin-erasure-apply-dialog-cancel-btn">
              Cancel
            </Button>
            <Button variant="destructive" onClick={submitApply} disabled={applying}
                    data-testid="admin-erasure-apply-dialog-confirm-btn">
              {applying && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Apply erasure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent data-testid="admin-restore-apply-dialog">
          <DialogHeader>
            <DialogTitle>Confirm restore reconciliation</DialogTitle>
            <DialogDescription>
              Type RECONCILE to confirm running the restore reconciliation.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="restore-confirm">Type RECONCILE</Label>
            <Input id="restore-confirm" value={restoreConfirm}
                   onChange={e => setRestoreConfirm(e.target.value)}
                   data-testid="admin-restore-apply-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreDialogOpen(false)}
                    data-testid="admin-restore-apply-dialog-cancel-btn">
              Cancel
            </Button>
            <Button variant="destructive" onClick={submitRestoreApply}
                    data-testid="admin-restore-apply-dialog-confirm-btn">
              Apply reconciliation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
